import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';

const PURPLE = '#a100aa';

export default function VerifyEmailPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, verifyCode, resendVerifyCode } = useAuth();
  const isKo = i18n.language === 'ko';

  // Resolve the target email: router state > logged-in user > query string
  const queryEmail = new URLSearchParams(location.search).get('email');
  const targetEmail = location.state?.email || user?.email || queryEmail || '';

  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [cooldown, setCooldown] = useState(0); // seconds remaining before resend allowed
  const inputsRef = useRef([]);

  // If user is already verified, skip this page.
  useEffect(() => {
    if (user?.email_verified_at) navigate('/app', { replace: true });
  }, [user, navigate]);

  // Initial focus
  useEffect(() => {
    inputsRef.current[0]?.focus();
  }, []);

  // Countdown ticker
  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setTimeout(() => setCooldown(c => Math.max(0, c - 1)), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  const code = digits.join('');

  const setDigitAt = (idx, value) => {
    setDigits(prev => {
      const next = [...prev];
      next[idx] = value;
      return next;
    });
  };

  const handleChange = (idx, raw) => {
    const v = (raw || '').replace(/\D/g, '').slice(0, 1);
    setDigitAt(idx, v);
    if (v && idx < 5) inputsRef.current[idx + 1]?.focus();
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && idx > 0) inputsRef.current[idx - 1]?.focus();
    if (e.key === 'ArrowRight' && idx < 5) inputsRef.current[idx + 1]?.focus();
  };

  const handlePaste = (e) => {
    const text = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, 6);
    if (!text) return;
    e.preventDefault();
    const arr = text.split('');
    const next = ['', '', '', '', '', ''];
    for (let i = 0; i < 6; i++) next[i] = arr[i] || '';
    setDigits(next);
    const focusIdx = Math.min(text.length, 5);
    inputsRef.current[focusIdx]?.focus();
  };

  const handleSubmit = useCallback(async (e) => {
    if (e) e.preventDefault();
    if (code.length !== 6 || submitting) return;
    setError('');
    setInfo('');
    setSubmitting(true);
    try {
      await verifyCode(targetEmail, code);
      navigate('/verified', { replace: true });
    } catch (err) {
      if (err.code === 'CODE_EXPIRED') {
        setError(isKo ? '코드가 만료되었습니다. 다시 요청해주세요.' : 'Code expired. Please request a new one.');
      } else if (err.code === 'TOO_MANY_ATTEMPTS') {
        setError(isKo ? '시도 횟수를 초과했습니다. 새 코드를 요청해주세요.' : 'Too many attempts. Please request a new code.');
      } else if (err.attemptsRemaining != null) {
        setError(isKo
          ? `코드가 올바르지 않습니다. 남은 시도: ${err.attemptsRemaining}회`
          : `Incorrect code. Attempts remaining: ${err.attemptsRemaining}`);
      } else {
        setError(err.message || (isKo ? '인증에 실패했습니다' : 'Verification failed'));
      }
    } finally {
      setSubmitting(false);
    }
  }, [code, submitting, verifyCode, targetEmail, navigate, isKo]);

  // Auto-submit when all 6 digits entered
  useEffect(() => {
    if (code.length === 6 && !submitting) handleSubmit();
  }, [code, submitting, handleSubmit]);

  const handleResend = async () => {
    if (cooldown > 0) return;
    setError('');
    setInfo('');
    try {
      await resendVerifyCode(targetEmail);
      setInfo(isKo ? '새 인증 코드를 전송했습니다.' : 'A new verification code has been sent.');
      setCooldown(60);
      setDigits(['', '', '', '', '', '']);
      inputsRef.current[0]?.focus();
    } catch (err) {
      if (err.retryAfter) {
        setCooldown(Number(err.retryAfter) || 60);
      }
      setError(err.message || (isKo ? '재전송에 실패했습니다' : 'Failed to resend code'));
    }
  };

  if (!targetEmail) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ backgroundColor: '#0D1117', color: '#E6EDF3' }}>
        <div className="text-center">
          <p>{isKo ? '인증할 이메일을 찾을 수 없습니다.' : 'No email to verify.'}</p>
          <button onClick={() => navigate('/login')} style={{ marginTop: 16, color: PURPLE }}>
            {isKo ? '로그인으로 돌아가기' : 'Back to login'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center relative p-6" style={{ backgroundColor: '#0D1117' }}>
      <div className="fixed inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 0 }}>
        <div style={{ position: 'absolute', top: '-10%', right: '-5%', width: '40%', height: '40%', background: 'radial-gradient(circle, rgba(161,0,170,0.15) 0%, rgba(161,0,170,0) 70%)', filter: 'blur(80px)' }} />
        <div style={{ position: 'absolute', bottom: '-10%', left: '-5%', width: '40%', height: '40%', background: 'radial-gradient(circle, rgba(0,51,153,0.2) 0%, rgba(0,51,153,0) 70%)', filter: 'blur(80px)' }} />
      </div>

      <div style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 440 }}>
        <div style={{
          background: 'rgba(22, 27, 34, 0.8)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 16,
          padding: '40px 32px',
          color: '#E6EDF3',
        }}>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, textAlign: 'center' }}>
            {isKo ? '이메일 주소를 확인해주세요' : 'Verify your email'}
          </h1>
          <p style={{ margin: '12px 0 28px', textAlign: 'center', color: '#8B949E', fontSize: 14 }}>
            {isKo
              ? <>인증 코드를 <strong style={{ color: '#E6EDF3' }}>{targetEmail}</strong>로 전송했습니다.<br />메일함을 확인하고 6자리 코드를 입력해주세요.</>
              : <>We sent a 6-digit code to <strong style={{ color: '#E6EDF3' }}>{targetEmail}</strong>.<br />Enter it below to continue.</>}
          </p>

          <form onSubmit={handleSubmit}>
            <div onPaste={handlePaste} style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 20 }}>
              {digits.map((d, i) => (
                <input
                  key={i}
                  ref={el => (inputsRef.current[i] = el)}
                  type="text"
                  inputMode="numeric"
                  pattern="\d*"
                  maxLength={1}
                  value={d}
                  onChange={(e) => handleChange(i, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(i, e)}
                  disabled={submitting}
                  aria-label={`Digit ${i + 1}`}
                  style={{
                    width: 48,
                    height: 56,
                    textAlign: 'center',
                    fontSize: 24,
                    fontWeight: 700,
                    color: '#E6EDF3',
                    background: 'rgba(13,17,23,0.6)',
                    border: `1.5px solid ${d ? PURPLE : 'rgba(255,255,255,0.15)'}`,
                    borderRadius: 10,
                    outline: 'none',
                    transition: 'border-color 120ms ease',
                  }}
                />
              ))}
            </div>

            {error && (
              <div role="alert" style={{ color: '#F87171', fontSize: 13, textAlign: 'center', marginBottom: 16 }}>
                {error}
              </div>
            )}
            {info && !error && (
              <div style={{ color: '#34D399', fontSize: 13, textAlign: 'center', marginBottom: 16 }}>
                {info}
              </div>
            )}

            <button
              type="submit"
              disabled={code.length !== 6 || submitting}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: 'none',
                borderRadius: 10,
                background: code.length === 6 ? PURPLE : 'rgba(161,0,170,0.35)',
                color: '#fff',
                fontWeight: 600,
                fontSize: 15,
                cursor: code.length === 6 && !submitting ? 'pointer' : 'not-allowed',
              }}
            >
              {submitting
                ? (isKo ? '확인 중…' : 'Verifying…')
                : (isKo ? '이메일 인증하기' : 'Verify email')}
            </button>
          </form>

          <div style={{ marginTop: 24, textAlign: 'center', fontSize: 13, color: '#8B949E' }}>
            {isKo ? '코드를 받지 못하셨나요?' : "Didn't receive the code?"}{' '}
            <button
              type="button"
              onClick={handleResend}
              disabled={cooldown > 0}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                color: cooldown > 0 ? '#6E7681' : PURPLE,
                cursor: cooldown > 0 ? 'not-allowed' : 'pointer',
                fontWeight: 600,
              }}
            >
              {cooldown > 0
                ? (isKo ? `${cooldown}초 후 재전송 가능` : `Resend in ${cooldown}s`)
                : (isKo ? '코드 다시 보내기' : 'Resend code')}
            </button>
          </div>
          <div style={{ marginTop: 8, textAlign: 'center', fontSize: 12, color: '#6E7681' }}>
            {t && ''}
            <button
              type="button"
              onClick={() => navigate('/login')}
              style={{ background: 'none', border: 'none', padding: 0, color: '#8B949E', cursor: 'pointer', textDecoration: 'underline' }}
            >
              {isKo ? '다른 이메일로 로그인' : 'Sign in with a different email'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
